/**
 * Value Gap Details Modal
 * Displays detailed breakdown of value gap (business attractiveness) calculation
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TrendingUp, X, Download } from 'lucide-react';
import { formatCurrency } from '../../utils/enhancedDashboardUtils';
import { useBusinessContext } from '../../contexts/BusinessContext';

const ValueGapDetailsModal = ({ show, onClose, valueGap }) => {
  const { currentBusinessId } = useBusinessContext();
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  const handleDownloadPDF = async () => {
    if (!currentBusinessId) {
      alert('No business selected');
      return;
    }

    setDownloadingPDF(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/pdf/value-gap-details/${currentBusinessId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Business-Id': currentBusinessId
        }
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : 'Value_Gap_Details.pdf';

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (!show || !valueGap) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-full sm:max-w-2xl md:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Header - Responsive */}
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 p-4 sm:p-6 flex justify-between items-center z-10">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <TrendingUp size={24} className="text-white flex-shrink-0 sm:w-7 sm:h-7" />
            <div className="min-w-0">
              <h3 className="text-lg sm:text-2xl font-bold text-white truncate">Value Gap Details</h3>
              <p className="text-emerald-100 text-xs sm:text-sm hidden sm:block">Business Attractiveness Analysis</p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}
              className="text-white/90 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 flex items-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed min-w-[44px] min-h-[44px]"
              title="Download PDF"
            >
              <Download className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">
                {downloadingPDF ? 'Generating...' : 'PDF'}
              </span>
            </button>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors bg-white/10 hover:bg-white/20 rounded-lg p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content - Responsive padding */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Summary */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <h4 className="font-bold text-lg text-gray-900 mb-3">Summary</h4>
            <p className="text-gray-700 text-sm mb-4">
              Your value gap measures the additional business value you could unlock by improving your business gap assessment score.
            </p>

            {/* Two scenarios side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Green Zone Scenario */}
              <div className="bg-white rounded-lg p-4 border border-blue-300">
                <p className="text-sm font-semibold text-blue-900 mb-2">At Green Zone Score</p>
                <p className="text-xs text-blue-700 mb-3">({valueGap.green_zone_attractiveness?.toFixed(1)}% business gap assessment score)</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-blue-700">Valuation Impact:</span>
                  <span className="text-2xl font-bold text-blue-600">
                    {formatCurrency(valueGap.value_impact_green_zone)}
                  </span>
                </div>
              </div>

              {/* Best in Class Scenario */}
              <div className="bg-white rounded-lg p-4 border border-green-300">
                <p className="text-sm font-semibold text-green-900 mb-2">At Best in Class Score</p>
                <p className="text-xs text-green-700 mb-3">({valueGap.best_in_class_attractiveness?.toFixed(1)}% business gap assessment score)</p>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-700">Valuation Impact:</span>
                  <span className="text-2xl font-bold text-green-600">
                    {formatCurrency(valueGap.value_impact_best_in_class)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Valuation Range */}
          <div>
            <h4 className="font-bold text-lg text-gray-900 mb-3">Valuation Range</h4>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Low Range:</span>
                <span className="font-semibold">{formatCurrency(valueGap.valuation_low)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">High Range:</span>
                <span className="font-semibold">{formatCurrency(valueGap.valuation_high)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-300">
                <span className="text-sm text-gray-600">Range Spread:</span>
                <span className="font-semibold">{formatCurrency(valueGap.valuation_range)}</span>
              </div>
            </div>
          </div>

          {/* Attractiveness Scores */}
          <div>
            <h4 className="font-bold text-lg text-gray-900 mb-3">Business Gap Assessment Scores</h4>
            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Current Score</p>
                  <p className="text-2xl font-bold text-amber-600">{valueGap.current_attractiveness?.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Green Zone Score</p>
                  <p className="text-2xl font-bold text-blue-600">{valueGap.green_zone_attractiveness?.toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Best in Class Score</p>
                  <p className="text-2xl font-bold text-emerald-600">{valueGap.best_in_class_attractiveness?.toFixed(1)}%</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-indigo-200">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Gap to Green Zone</p>
                  <p className="text-lg font-bold text-blue-600">
                    {(valueGap.green_zone_attractiveness - valueGap.current_attractiveness).toFixed(1)} percentage points
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Gap to Best in Class</p>
                  <p className="text-lg font-bold text-green-600">
                    {(valueGap.best_in_class_attractiveness - valueGap.current_attractiveness).toFixed(1)} percentage points
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step-by-Step Calculation */}
          <div>
            <h4 className="font-bold text-lg text-gray-900 mb-3">Step-by-Step Calculations</h4>
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-indigo-900 font-semibold mb-1">Assessment Score Scaling (17% - 100%)</p>
              <p className="text-xs text-indigo-800">
                Assessment scores range from <span className="font-bold">17% (minimum)</span> to <span className="font-bold">100% (maximum)</span>.
                A <span className="font-bold">58% assessment</span> represents an average business at approximately the <span className="font-bold">midpoint (49%)</span> of your valuation range.
              </p>
              <p className="text-xs text-indigo-800 mt-1">
                Linear scaling: 17% → 0% (low), 58% → 49% (midpoint), 67% → 60% (green zone), 72% → 66% (best-in-class), 100% → 100% (high)
              </p>
            </div>

            {/* Current Value Calculation */}
            <div className="mb-6">
              <h5 className="font-semibold text-md text-gray-900 mb-3">Current Exit Value</h5>
              <div className="space-y-3">
                <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Step 1: Scale Current Assessment Score</p>
                  <p className="text-sm text-gray-800">
                    Valuation Position = (Assessment Score - 17) / 83 × 100
                  </p>
                  <p className="text-sm text-gray-800 mt-1">
                    = ({valueGap.current_attractiveness?.toFixed(1)}% - 17) / 83 × 100
                  </p>
                  <p className="text-md font-bold text-gray-900 mt-1">
                    = {(((valueGap.current_attractiveness - 17) / 83) * 100).toFixed(1)}%
                  </p>
                </div>

                <div className="bg-gray-50 border-l-4 border-gray-500 p-4 rounded">
                  <p className="text-sm font-semibold text-gray-900 mb-1">Step 2: Calculate Current Exit Value</p>
                  <p className="text-sm text-gray-800">
                    Current Exit Value = Low + (Range × Valuation Position)
                  </p>
                  <p className="text-sm text-gray-800 mt-2">
                    = {formatCurrency(valueGap.valuation_low)} + ({formatCurrency(valueGap.valuation_range)} × {(((valueGap.current_attractiveness - 17) / 83) * 100).toFixed(1)}%)
                  </p>
                  <p className="text-2xl font-bold text-gray-900 mt-2">
                    = {formatCurrency(valueGap.current_exit_value)}
                  </p>
                </div>
              </div>
            </div>

            {/* Green Zone Scenario */}
            <div className="mb-6">
              <h5 className="font-semibold text-md text-blue-900 mb-3">Scenario 1: At Green Zone Score ({valueGap.green_zone_attractiveness?.toFixed(1)}%)</h5>
              <div className="space-y-3">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 1: Scale Green Zone Assessment Score</p>
                  <p className="text-sm text-blue-800">
                    Valuation Position = (Assessment Score - 17) / 83 × 100
                  </p>
                  <p className="text-sm text-blue-800 mt-1">
                    = ({valueGap.green_zone_attractiveness?.toFixed(1)}% - 17) / 83 × 100
                  </p>
                  <p className="text-md font-bold text-blue-900 mt-1">
                    = {(((valueGap.green_zone_attractiveness - 17) / 83) * 100).toFixed(1)}%
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 2: Calculate Green Zone Value</p>
                  <p className="text-sm text-blue-800">
                    Green Zone Value = Low + (Range × Valuation Position)
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    = {formatCurrency(valueGap.valuation_low)} + ({formatCurrency(valueGap.valuation_range)} × {(((valueGap.green_zone_attractiveness - 17) / 83) * 100).toFixed(1)}%)
                  </p>
                  <p className="text-2xl font-bold text-blue-900 mt-2">
                    = {formatCurrency(valueGap.green_zone_value)}
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                  <p className="text-sm font-semibold text-blue-900 mb-1">Step 3: Calculate Valuation Impact</p>
                  <p className="text-sm text-blue-800">
                    Impact = Green Zone Value - Current Exit Value
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    = {formatCurrency(valueGap.green_zone_value)} - {formatCurrency(valueGap.current_exit_value)}
                  </p>
                  <p className="text-2xl font-bold text-blue-900 mt-2">
                    = {formatCurrency(valueGap.value_impact_green_zone)}
                  </p>
                </div>
              </div>
            </div>

            {/* Best in Class Scenario */}
            <div>
              <h5 className="font-semibold text-md text-green-900 mb-3">Scenario 2: At Best in Class Score ({valueGap.best_in_class_attractiveness?.toFixed(1)}%)</h5>
              <div className="space-y-3">
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 1: Scale Best in Class Assessment Score</p>
                  <p className="text-sm text-green-800">
                    Valuation Position = (Assessment Score - 17) / 83 × 100
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    = ({valueGap.best_in_class_attractiveness?.toFixed(1)}% - 17) / 83 × 100
                  </p>
                  <p className="text-md font-bold text-green-900 mt-1">
                    = {(((valueGap.best_in_class_attractiveness - 17) / 83) * 100).toFixed(1)}%
                  </p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 2: Calculate Best in Class Value</p>
                  <p className="text-sm text-green-800">
                    Best in Class Value = Low + (Range × Valuation Position)
                  </p>
                  <p className="text-sm text-green-800 mt-2">
                    = {formatCurrency(valueGap.valuation_low)} + ({formatCurrency(valueGap.valuation_range)} × {(((valueGap.best_in_class_attractiveness - 17) / 83) * 100).toFixed(1)}%)
                  </p>
                  <p className="text-2xl font-bold text-green-900 mt-2">
                    = {formatCurrency(valueGap.best_in_class_value)}
                  </p>
                </div>

                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-sm font-semibold text-green-900 mb-1">Step 3: Calculate Valuation Impact</p>
                  <p className="text-sm text-green-800">
                    Impact = Best in Class Value - Current Exit Value
                  </p>
                  <p className="text-sm text-green-800 mt-2">
                    = {formatCurrency(valueGap.best_in_class_value)} - {formatCurrency(valueGap.current_exit_value)}
                  </p>
                  <p className="text-2xl font-bold text-green-900 mt-2">
                    = {formatCurrency(valueGap.value_impact_best_in_class)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Interpretation */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-bold text-gray-900 mb-3">What This Means</h4>

            <div className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-blue-900">Green Zone Scenario:</span> By improving your business gap assessment score from <span className="font-semibold">{valueGap.current_attractiveness?.toFixed(1)}%</span> to
                  the green zone level of <span className="font-semibold">{valueGap.green_zone_attractiveness?.toFixed(1)}%</span>,
                  you could add <span className="font-semibold text-blue-600">{formatCurrency(valueGap.value_impact_green_zone)}</span> to your exit value.
                </p>
              </div>

              <div className="p-3 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-gray-700">
                  <span className="font-semibold text-green-900">Best in Class Scenario:</span> By improving your business gap assessment score to
                  best-in-class level of <span className="font-semibold">{valueGap.best_in_class_attractiveness?.toFixed(1)}%</span>,
                  you could add <span className="font-semibold text-green-600">{formatCurrency(valueGap.value_impact_best_in_class)}</span> to your exit value.
                </p>
              </div>
            </div>

            <p className="text-sm text-gray-700 mt-4">
              Focus on improving your business gap assessment scores by addressing weaknesses in operations, financials, customer concentration, and growth potential.
              Even reaching the green zone ({valueGap.green_zone_attractiveness?.toFixed(1)}%)
              represents a {(valueGap.green_zone_attractiveness - valueGap.current_attractiveness).toFixed(1)} percentage point improvement
              and significant value creation opportunity.
            </p>
          </div>
        </div>

        {/* Footer - Responsive */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold transition min-h-[44px]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

ValueGapDetailsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  valueGap: PropTypes.shape({
    value_gap: PropTypes.number,
    valuation_low: PropTypes.number,
    valuation_high: PropTypes.number,
    valuation_range: PropTypes.number,
    current_attractiveness: PropTypes.number,
    green_zone_attractiveness: PropTypes.number,
    best_in_class_attractiveness: PropTypes.number,
    current_exit_value: PropTypes.number,
    green_zone_value: PropTypes.number,
    best_in_class_value: PropTypes.number,
    value_impact_green_zone: PropTypes.number,
    value_impact_best_in_class: PropTypes.number
  })
};

export default ValueGapDetailsModal;
